import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { signOut, upsertStudentParentPriors, updateStudent, fetchPlacementResultsForStudent } from "./supabaseClient.js";
// v1 mastery/review writers (createSkillState, updateSkillState,
// createReviewRecord, recordReview, updateFactPerformance) were removed
// when v2's applySessionResultsToStudent became the sole authority on
// per-skill mastery + review scheduling. App.jsx no longer touches
// masteryTracker. (The legacy DashboardScreen that read STATES from it
// was deleted in H-A5.)
import { defaultsForAge, applyPriorBoost } from "./engine/placementEngine.ts";
import { updatePlacementValidation } from "./engine/placementValidation.ts";
// New learning engine: mastery formula + adaptive promotion/walkback
import { applySessionResultsToStudent } from "./engine/sessionAdapter";
import { loadStudent, saveStudent } from "./engine/studentPersistence";
import { suggestedSupportLevel } from "./engine/adaptiveEngine";
import { migrateLegacyZeroBug } from "./engine/legacyMigration";
import { LEGACY_MODULE_TO_SKILL } from "./engine/skillGraph";
import { COLORS } from "./styles/theme.js";
// Critical path for a signed-out visitor: LoginScreen ships in the
// initial bundle so first paint of the front door is fast. (The legacy
// HomeScreen/SessionScreen imports were deleted in H-A5 — dormant since
// the Phase A flip routed / and /session to redirects.)
import LoginScreen from "./screens/LoginScreen.tsx";
import PrivateBetaScreen from "./screens/PrivateBetaScreen.tsx";
// v3 engine entry-point UI was removed in commit 169f1d4 (engine pivot
// A); v3 engine code itself was deleted in Phase A §A1 (2026-05-13).
// v1 is the live UX. Adaptive-engine v2 is being built on the
// feature/adaptation-engine branch per the spec at
// ~/Desktop/for Claude/PencilWise Adaptation and Progression Engine Spec.md.

// Onboarding / WhosPlaying are first-run-only or multi-child-only
// screens. A returning kid never hits them, so the extra roundtrip on
// first-time use is the right trade.
//
// MA-155 (retake-placement-wiring): the legacy PlacementScreen +
// needsPlacement gate were removed. That flow was already broken (see
// the decision log) — its "Start quick check" button navigated into
// /play/session, which never resolves the placement domain, so
// placementStatus stuck at "in_progress" forever. PlacementCheckCard
// (ParentSettingsSections.tsx) is the one live retake/recheck surface.
const WhosPlayingScreen  = lazy(() => import("./screens/WhosPlayingScreen.tsx"));
const MascotPickScreen   = lazy(() => import("./screens/MascotPickScreen.tsx"));

// Phase G1: AppRouter (screen-string switch) replaced by TanStack
// Router file-based routes under src/routes/. App.jsx keeps the gate
// ladder + state + handlers and renders <Outlet /> when gates pass.
// AppStateProvider shares state with the route tree; the legacy
// `setScreen(name)` API is preserved via a router.navigate shim so
// handlers in this file (and in useSessionLifecycle) don't need
// rewriting. G3 cleanup will progressively replace setScreen call
// sites with direct Link/navigate usage.
import { Outlet, useRouter, useLocation } from "@tanstack/react-router";
import { AppStateProvider } from "./App/AppStateContext.tsx";
import { BottomTabBar } from "./components/BottomTabBar.tsx";
import { KioskModeChip } from "./components/KioskModeChip.tsx";
import { useFitToViewport, FitFreezeKeyContext } from "./components/FitToViewport.tsx";
import { makeSetScreen, pathToScreen, SESSION_PATHS } from "./App/navigation.js";
import { Loading } from "./App/Loading.jsx";
import Toast from "./components/feedback/Toast.jsx";
import { MascotProvider } from "./context/MascotContext.jsx";
import { useToast } from "./context/ToastContext.jsx";
import useSessionLifecycle from "./App/useSessionLifecycle.js";
import { getConceptCard, shouldShowConceptCard, recordConceptCardShown } from "./engine/conceptCards";
import {
  listChildren as listChildrenStore,
  listChildrenForUser as listChildrenForUserStore,
  isKioskTaggedChild,
  claimUnownedChildren as claimUnownedChildrenStore,
  linkChildToSupabaseStudent as linkChildToSupabaseStudentStore,
  autoLinkByNameAge as autoLinkByNameAgeStore,
  getActiveChildId,
  setActiveChildId as setActiveChildIdStore,
  addChild as addChildStore,
  removeChild as removeChildStore,
  clearSupabaseStudentLink as clearSupabaseStudentLinkStore,
  saveProgressForChild,
  loadProgressForChild,
  resetProgressForChild,
  updateChildMeta,
} from "./childrenStore.js";
import useAuth from "./App/useAuth.js";
import useAllowlist from "./App/useAllowlist.js";
import useCloudSync from "./App/useCloudSync.js";
import useActiveStudentId from "./App/useActiveStudentId.js";
import { pickOnboardingReuseRow, doubleLinkReport, heldRowPayload } from "./App/sharedRowGuard.js";
import { defaultProgress, loadProgressForActiveChild, nextModuleProvenance, offlineBannerFirstName } from "./App/progress.ts";
import { markStoryCompleted } from "./content/stories.ts";
import { captureSupabaseError } from "./lib/sentry.ts";
import { qaRoutesEnabled } from "./lib/qaRoutes.ts";
import { classifyGatePath, noteGatePath } from "./lib/parentGate.ts";
import { captureNonceAttr, buildShaAttr, authStateAttr, frontDoorLandingAttr, qaLocalInitAttr } from "./lib/qaCaptureHooks.ts";
import { computeEngineModuleAdvances } from "./App/moduleAdvancement.js";
import { hydrateLocalFromCloud } from "./App/crossDeviceHydration.js";
import { shouldShowMascotGate } from "./App/mascotGate.js";
import { shouldHoldForCloudError, shouldExitOnboardingAfterHydration, shouldHoldZeroActive } from "./App/onboardingHoldGuard.js";
import { shouldHoldForBoot } from "./App/bootGateGuard.js";
import CloudErrorRetry from "./App/CloudErrorRetry.jsx";
import { setOnboardingDraftUser } from "./lib/onboardingDraft.ts";
import { useFamilyAddKidGateBlocked } from "./lib/teacherAddKidGate.ts";
import { useOutboxDrain } from "./lib/engine/useOutboxDrain";
import { useOutboxHolding } from "./lib/engine/useOutboxHolding";
import { useQueryClient } from "@tanstack/react-query";
import { qk } from "./lib/engine/queryKeys.ts";
import { mergeCloudStudentIntoProgress } from "./App/mergeCloudStudent.js"; // MA-858 (D-1008-28)
import { syncPendingAge, onboardingAgePending } from "./App/agePullMerge.js"; // MA-771
import { restoreProvenanceForChild } from "./App/placementProvenanceRestore.ts";
import { syncPendingAutoRead, noteAutoReadCloudValue, isChildLinkedFor } from "./App/autoReadPullMerge.js";
import { withMascotPick, onboardingMascotPending, confirmPickSync, pushPendingMascot } from "./App/mascotPullMerge.js"; // MA-1024
import { registerOnlineCallback } from "./lib/reachability.ts";
import { shouldMergeCloudStudent, stampKey } from "./App/cloudMergeGuard.js";
import { createProgressWriter } from "./App/progressWriter.js"; // MA-831
import { pushLevelsWithRetry } from "./lib/engine/levelsPushRetry.ts";
import { pushAssignedSkillsWithRetry } from "./lib/engine/assignedSkillsPushRetry.ts";
import { pushSuppressedSkillsWithRetry } from "./lib/engine/suppressedSkillsPushRetry.ts";
import { OfflineBanner, offlineInsetLayout } from "./components/OfflineBanner.tsx";
import { applyViewportMeta } from "./lib/viewportMeta.ts";

const STORAGE_KEY = "pencilwise-v4-engine"; // legacy single-child key, kept for migration only
const PLACEMENT_REFRESH_DAYS = 14; // re-suggest placement check every N days

// legacyModuleForSkill / moduleListFor / moduleAtOffset /
// computeEngineModuleAdvances live in src/App/moduleAdvancement.js.

// defaultProgress / loadProgressForActiveChild live in src/App/progress.js.

// useAuth + useCloudSync live in src/App/{useAuth,useCloudSync}.js.

// ── Root App ──────────────────────────────────────────────────

// One-shot legacy migration: clears any cached items shaped like the
// pre-validator 0+0 bug Sasha reported. Idempotent — runs once per
// browser, then writes pw.migration.zeroBugCleared=1 and skips.
// Defensively wrapped: a throw here at module-load time would prevent
// React from mounting at all.
try { migrateLegacyZeroBug(); } catch (err) { console.warn("legacy migration failed (non-fatal):", err); }

export default function App() {
  const { user, loading: authLoading } = useAuth();
  // MA-544 — retires the family add-kid workaround once this account owns a
  // class (see teacherAddKidGate.ts's own header). Called unconditionally
  // here, alongside useAuth/useAllowlist (rules of hooks) — the gate ladder
  // below has many early returns, so this MUST resolve before any of them,
  // not inside the ladder itself. Role-check-first inside the gate means a
  // parent-role account's result is `false` from the first render and never
  // flips — the WhosPlayingScreen add-tile stays byte-untouched for them.
  const familyAddKidBlocked = useFamilyAddKidGateBlocked(user?.id ?? null);
  // MA-03b: private-beta allowlist gate. Must be called unconditionally
  // (rules of hooks). When user is null the hook returns loading:false/
  // allowed:false immediately (no query). The ?dev=1 bypass short-circuits
  // to allowed:true. allowlistLoading is included in the combined loading
  // guard below so no post-auth content renders before the check settles.
  const {
    loading: allowlistLoading,
    allowed: allowlisted,
    outage: allowlistOutage,
    error: allowlistError,
    retry: retryAllowlistCheck,
  } = useAllowlist(user);
  // Toast lives in context (provider mounted in main.jsx) so we can read
  // showToast before useCloudSync runs — no more ref-shuffling for the
  // cloud-offline callback.
  const { toast, showToast, dismissToast } = useToast();
  // Fire the "cloud sync went offline" message once per offline transition.
  // The ref is just the dedupe latch; the callback identity is stable.
  const cloudOfflineToldRef = useRef(false);
  // MA-750 — the OfflineBanner's measured height (0 when it isn't shown);
  // the app frame reserves that strip so the banner never covers a control.
  // It is also the ONE-offline-signal switch: while the banner is on screen
  // it already says "you're offline — progress will sync", so the cloud
  // toast and the bottom-left pill stay quiet. They still speak where the
  // banner is not: the device is online but cloud sync is failing, or a
  // gate screen (sign-in, cloud retry, loading) that never mounts it.
  const [offlineInset, setOfflineInset] = useState(0);
  const offlineBannerShowing = offlineInset > 0;
  // MA-760 — publish the banner's height as `--offline-inset` on <html> (and
  // a marker attribute) so the full-height screens (min-h-screen/min-h-dvh
  // roots, globals.css) fit the height the banner leaves instead of growing
  // by the strip. Removed again online: the online DOM is unchanged.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (offlineInset > 0) {
      root.style.setProperty("--offline-inset", `${offlineInset}px`);
      root.setAttribute("data-offline-inset", "");
    } else {
      root.style.removeProperty("--offline-inset");
      root.removeAttribute("data-offline-inset");
    }
    return () => {
      root.style.removeProperty("--offline-inset");
      root.removeAttribute("data-offline-inset");
    };
  }, [offlineInset]);
  const offlineBannerShowingRef = useRef(offlineBannerShowing);
  offlineBannerShowingRef.current = offlineBannerShowing;
  // P-CROSS-DEVICE-HYDRATION (#20): one-time-per-sign-in latch for the
  // cloud→local hydration effect below. Without it, every render that still
  // satisfies the hydration conditions (between the createChild write and the
  // setRegistry re-read) would re-hydrate and duplicate locals. Reset on
  // user-id change in the [user?.id, authLoading] effect so a different
  // account on the same device re-evaluates from scratch.
  const hasHydratedThisSessionRef = useRef(false);
  const doubleLinkSeenRef = useRef(new Set());
  const handleCloudOffline = useCallback(() => {
    if (offlineBannerShowingRef.current) return; // MA-750: the banner is already saying it
    if (cloudOfflineToldRef.current) return;
    cloudOfflineToldRef.current = true;
    showToast("Cloud sync is offline — your progress is saved on this device and will sync when reconnected.", "info", 6000);
  }, [showToast]);
  // Local activeChildId is read from a ref because useCloudSync needs the
  // active link to derive cloudStudent — but we also use cloudStudent
  // inside other state declarations below. Sequencing: declare registry
  // / activeChildId first, then useCloudSync reads activeChild.supabaseStudentId
  // via the registry below.
  // Phase G1: `screen` is derived from the current URL via TanStack
  // Router instead of useState. `setScreen(name)` is a shim that
  // navigates to the matching path. Behavior identical to the prior
  // string-state machine for every existing call site.
  const router = useRouter();
  const location = useLocation();
  // MA-61 assign slice — invalidates the todays-mission cache when the
  // cloud->local assignedSkillIds merge below actually adds something new.
  const queryClient = useQueryClient();
  // Page-fit shell as a HOOK (called unconditionally, before early returns) so
  // the session shell can be a plain <div> for clean route reconciliation.
  const __isSessionPath = SESSION_PATHS.has(location.pathname);
  const fitViewport = useFitToViewport(__isSessionPath);
  const screen = pathToScreen(location.pathname);
  const setScreen = useMemo(() => makeSetScreen(router), [router]);
  // MA-853 (D-PUX-3): leaving the parent area (/parent, /teacher) for a kid
  // surface clears the grown-up gate's unlock; legal pages and /onboarding
  // do not (noteGatePath in lib/parentGate.ts holds the route classes).
  useEffect(() => { noteGatePath(location.pathname); }, [location.pathname]);
  // Multi-child registry state. Filtered to children owned by the current
  // signed-in user — a shared browser where Account A then Account B both
  // sign in won't surface A's kids to B. Initial value is empty until
  // user.id resolves; the useEffect below claims unowned children for
  // this user (one-shot, back-compat for pre-tagging) and populates the
  // registry. activeChildId === null means show WhosPlayingScreen (or
  // auto-pick if exactly one child exists for this user).
  const [registry, setRegistry] = useState({ children: [], activeChildId: null });
  const [activeChildId, setActiveChildIdStateRaw] = useState(null);
  // MA-792 (RED R2-S1) — the active child id, updated SYNCHRONOUSLY by the one
  // setter every switch path goes through, so an async cloud-write completion
  // never sees the previous child as "still active" between the switch and the
  // next render. Read through getLiveActiveChildId.
  const activeChildIdRef = useRef(null);
  const setActiveChildIdState = (v) => { activeChildIdRef.current = v; setActiveChildIdStateRaw(v); };
  const [progress, setProgressRaw] = useState(() => loadProgressForActiveChild(null));
  // MA-831 — every progress write goes through ONE synchronous writer: a
  // read-modify-write on the child's stored record (React-only keys and keys
  // whose last write failed are kept), saved under an explicit child id, and
  // handed to React only when that id is the live child. React progress is
  // only ever set through it (setProgressValue), never with an updater.
  // See App/progressWriter.js.
  const progressWriterRef = useRef(null);
  if (!progressWriterRef.current) {
    progressWriterRef.current = createProgressWriter({
      loadRaw: (id) => loadProgressForChild(id, () => null),
      save: saveProgressForChild,
      defaults: defaultProgress,
      getLiveChildId: () => activeChildIdRef.current,
      setReactValue: (v) => setProgressRaw(v),
      initialValue: progress,
    });
  }
  const progressWriter = progressWriterRef.current;
  const setProgressValue = (value, childId) => progressWriter.setValue(value, childId);
  const writeProgressForChild = (id, input) => progressWriter.write(id, input);
  // True when the parent explicitly chose "+ Add child" from Profiles or
  // WhosPlaying. Without this, the WhosPlaying gate (which fires when
  // there's no activeChildId AND children exist) would loop the user
  // back to the picker because clearing activeChildId is exactly the
  // condition that surfaces it. Reset to false only when the new child's
  // onboarding completes (handleOnboardingComplete / useFinishOnboarding's reuse
  // path) or on sign-out - NOT when the parent navigates away.
  const [addingChildIntent, setAddingChildIntent] = useState(false);

  // T0 cutover: the active child's progress loads in the [user?.id] effect
  // below (async, after the first paint). Until it has, `progress` is the
  // default (onboardingComplete=false). The old inline OnboardingScreen
  // tolerated that transient (it re-rendered to the right screen once
  // progress hydrated); the OnboardingRedirector does NOT — its one-way
  // navigate to /onboarding would trap a returning, already-onboarded kid.
  // Gate the whole ladder on this flag so it only evaluates settled state.
  const [bootHydrated, setBootHydrated] = useState(false);

  // Active child's link to its cloud row, used by useCloudSync to pick the
  // right Supabase student record. Recomputed on every render but stable
  // identity-wise since we read from registry which is stable per user.
  const activeChild = registry.children.find((c) => c.id === activeChildId) || null;
  const activeSupabaseStudentId = activeChild?.supabaseStudentId || null;

  const { students, studentsOwnerId, cloudStudent, cloudLoading, cloudOffline, cloudError, zeroActiveRecovery, syncToCloud, saveCloudSession, createCloudStudent, refreshStudents } =
    useCloudSync(user, activeSupabaseStudentId, handleCloudOffline);
  // MA-1024 — a pal pick's cloud write. syncToCloud sends mascot_id only while
  // progress.mascotPendingSync is set and resolves with the pal the returned
  // row confirmed; the flag is cleared for the child that picked (captured
  // now, so a kid switch mid-flight cannot clear another child's flag), and
  // only if that child's stored pal is still the confirmed one.
  const syncPickToCloud = (p) =>
    confirmPickSync({ sync: syncToCloud, progress: p, childId: activeChildIdRef.current, setProgressForChild: writeProgressForChild });

  // P-PATHHOME-BUG-2-ROUND-2 — canonical persistence identity. The
  // engine must read+write child_attempts under the cloud students.id
  // (FK + RLS target), NOT the local activeChildId. ensureActiveStudentId
  // resolves it for writes, lazy-creating + linking a cloud student on
  // the first attempt when the active child isn't linked yet (idempotent,
  // load-gated, failure-safe — see useActiveStudentId). `onLinked`
  // persists the link to the registry and re-reads it so subsequent
  // renders expose a non-null activeSupabaseStudentId to all reads.
  const handleStudentLinked = useCallback((localChildId, cloudStudentId) => {
    const linked = linkChildToSupabaseStudentStore(localChildId, cloudStudentId);
    if (linked === false) {
      // MA-800: the primitive refused (row held by another local kid). Surface it
      // (ids only) and tell the resolver so no write goes under that row.
      captureSupabaseError(
        "MA-800 link refused: cloud student row already linked to another local child",
        new Error("link refused"),
        heldRowPayload(listChildrenStore(), cloudStudentId, localChildId) ?? { groups: [] },
      );
      return false;
    }
    if (user?.id) setRegistry(listChildrenForUserStore(user.id));
    return true;
  }, [user?.id]);
  const ensureActiveStudentId = useActiveStudentId({
    user,
    activeChild,
    activeSupabaseStudentId,
    // P-DUP-STUDENT-FIX — pass the loaded cloud students so the resolver
    // can lookup-before-create (reuse an existing row instead of INSERTing
    // a duplicate when auto-link-by-name transiently missed).
    cloudStudents: students,
    progress,
    cloudLoading,
    createCloudStudent,
    onLinked: handleStudentLinked,
  });

  // MA-376 (c): a fully-offline stretch that outgrows the local mirror's
  // cap before ever syncing can evict an un-drained row — surface ONE calm
  // signal (same "info", non-alarming tone as handleCloudOffline above)
  // rather than losing that history with zero trace.
  const handleUndrainedEviction = useCallback(() => {
    showToast(
      "Some older activity on this device was cleared before it could sync to the cloud. Recent progress is safe and syncing normally.",
      "info",
      6000,
    );
  }, [showToast]);

  // MA-15: drain local attempt + milestone mirrors to cloud on reconnect.
  useOutboxDrain(activeChildId, activeSupabaseStudentId, handleUndrainedEviction);
  // MA-107: calm "Saving…" banner line while ONLINE but the outbox is holding writes.
  const outboxHolding = useOutboxHolding(activeChildId, activeSupabaseStudentId);

  // Claim + filter registry on user-id change. Runs once per sign-in.
  // Cross-account safety: if the previously-active child belonged to a
  // different account, the filtered registry won't contain it and we
  // reset activeChildId to null so the WhosPlayingScreen gate fires.
  useEffect(() => {
    // Wait for auth to settle before hydrating (and before marking
    // bootHydrated) — otherwise the transient `user===undefined` pass
    // would mark hydration done while progress is still the default,
    // re-opening the OnboardingRedirector bounce window.
    if (authLoading) return;
    // Reset the cross-device hydration latch on every settled auth change
    // (sign-out, or a different account signing in) so hydration can run once
    // for the new user. Mutations to the registry by user actions don't change
    // user?.id / authLoading, so this never re-arms mid-session.
    hasHydratedThisSessionRef.current = false;
    if (!user) { setRegistry({ children: [], activeChildId: null }); setActiveChildIdState(null); setAddingChildIntent(false); setBootHydrated(true); return; }
    claimUnownedChildrenStore(user.id);
    const filtered = listChildrenForUserStore(user.id);
    setRegistry(filtered);
    // MA-800: report (never repair) a registry where two local kids already
    // share one cloud row — once per distinct damage per session. Runs on sign-in
    // / load only, so damage created mid-session is reported on the next load.
    // The payload holds only the cloud row UUID + local ids (no name/age).
    const doubleLinkPayload = doubleLinkReport(filtered, doubleLinkSeenRef.current);
    if (doubleLinkPayload) {
      captureSupabaseError(
        "MA-800 double-linked registry: two local children share one cloud student row",
        new Error("double-linked registry"),
        doubleLinkPayload,
      );
    }
    const stored = getActiveChildId();
    const storedVisible = stored && filtered.children.some((c) => c.id === stored);
    const next = storedVisible ? stored : (filtered.children.length === 1 ? filtered.children[0].id : null);
    setActiveChildIdState(next);
    setProgressValue(loadProgressForActiveChild(next), next);
    setBootHydrated(true);
  }, [user?.id, authLoading]);

  // P-CROSS-DEVICE-HYDRATION (#20): hydrate the local registry from cloud on
  // a fresh-device sign-in. The gate ladder below derives `needsOnboarding`
  // from LOCAL state only — an empty local registry wrongly prompts
  // re-onboarding even when the cloud already has this parent's students
  // (the "logged in but still onboarding" gap). When the cloud has settled
  // with students AND local is empty for a signed-in user, recreate each
  // cloud student as a PRE-LINKED local child with onboardingComplete=true,
  // so the ladder sees a non-null cloudStudent + onboardingComplete progress
  // and skips onboarding. One-way (cloud→local) by design.
  //
  // Declared BEFORE the auto-link effect on purpose: both fire in the commit
  // where `students` first arrives. autoLinkByNameAge reads localStorage
  // directly (not the React registry state), so running hydration first means
  // the children it writes are already linked — auto-link then sees their
  // supabaseStudentId and correctly skips them. For a returning user (local
  // already populated) this effect is a pure no-op (registry.children > 0).
  useEffect(() => {
    if (!user || cloudLoading) return;
    if (hasHydratedThisSessionRef.current) return;
    if (students.length === 0 || registry.children.length > 0) return;

    const plan = hydrateLocalFromCloud(students, user, registry);
    if (!plan.shouldHydrate) return;

    // Latch BEFORE the writes so a synchronous re-entrancy (shouldn't happen,
    // but cheap insurance) can't double-create.
    hasHydratedThisSessionRef.current = true;
    let firstChildId = null;
    plan.children.forEach((c, i) => {
      const newChildId = addChildStore(c.addArgs);
      saveProgressForChild(newChildId, c.progress);
      if (i === plan.activeIndex) firstChildId = newChildId;
    });
    // addChildStore sets the store's activeChildId to the LAST child created;
    // override it to the oldest (firstChildId) so the persisted active matches
    // the in-memory selection after a reload (matters for multi-child).
    if (firstChildId) setActiveChildIdStore(firstChildId);
    setRegistry(listChildrenForUserStore(user.id));
    setActiveChildIdState(firstChildId);
    setProgressValue(loadProgressForActiveChild(firstChildId), firstChildId);
    console.warn(`[cross-device] hydrated ${plan.children.length} children from cloud into local registry`);
    // MA-394 DEFERRED (see BACKLOG.md decision log): a two-way /onboarding
    // redirect was attempted here (navigate("/") once real cross-device
    // hydration lands while the parent is already on /onboarding) and DROPPED
    // after live e2e testing proved it oscillates — the "/" front-door
    // redirect (src/routes/index.tsx) and the entry gate below raced across
    // several renders (/onboarding → / → /onboarding → /play → /onboarding)
    // and settled back on a hard reload to /onboarding in testing. Shipping a
    // redirect loop is worse than the stranding bug it would have fixed. Left
    // for a follow-up slice with a router-transition-safe approach (e.g. a
    // one-shot latched navigate gated on the router's OWN settled state, not
    // a raw effect dependency on location.pathname).
  }, [user?.id, cloudLoading, students, registry.children.length]);

  // Auto-link existing local children to their cloud students by name+age.
  // Runs whenever the cloud student list refreshes (initial load + after
  // createCloudStudent). Only links unambiguous matches — if two local
  // kids share a name, both stay unlinked rather than risk misrouting.
  useEffect(() => {
    if (!user || students.length === 0) return;
    const linked = autoLinkByNameAgeStore(user.id, students);
    if (linked > 0) setRegistry(listChildrenForUserStore(user.id));
  }, [user?.id, students]);
  // Session lifecycle (useSessionLifecycle.js) owns the activeSession +
  // pendingConcept + completedSession state cluster AND the handlers
  // (handleConceptAcknowledged, cancelConcept). App only destructures what
  // it actually uses now: handlePlacementStart/handlePlacementSkip (the
  // legacy PlacementScreen's onStart/onSkip) and the start/finish/goHome/
  // startNext handlers + completedSession were all zero-consumer dead code,
  // dropped in the MA-34 PR-1 dead-field cleanup (docs/adr/011) and MA-155's
  // retake-placement-wiring slice. App keeps the deps it injects.
  const {
    activeSession, activeStorySlug, pendingConcept,
    setActiveSession, setCompletedSession, setActiveStorySlug, setPendingConcept,
    clearActive: clearActiveSession,
    handleConceptAcknowledged,
    cancelConcept,
  } = useSessionLifecycle({
    setProgress: (updater) => setProgress(updater),
    setScreen,
  });

  // Reset the "told once" flag when cloud comes back online so we'll
  // notify again if it drops later in the session.
  useEffect(() => { if (!cloudOffline) cloudOfflineToldRef.current = false; }, [cloudOffline]);

  // P-AUTH-GATE-AND-ONBOARDING-DRAFT: scope the localStorage-backed onboarding
  // draft to the signed-in user so a refresh mid-onboarding rehydrates THIS
  // user's draft (and a shared device never cross-reads between accounts).
  // Binding on user-id change rehydrates from that user's key; sign-out
  // (null) drops back to an empty in-memory draft.
  useEffect(() => { setOnboardingDraftUser(user?.id ?? null); }, [user?.id]);

  // MA-831 — writes for the LIVE child at call time (not this render's closure
  // id), deciding on the stored record, not React's possibly stale copy.
  function setProgress(updater) {
    return progressWriter.write(activeChildIdRef.current, updater);
  }
  // P-READER-MARK-COMPLETED (closes #51 / audit finding #2): the kid
  // finishing a story on the reader's terminal page is the signal that
  // earns the "✓ Read" shelf stamp (reward-cadence research: progress
  // must be NAMED + VISIBLE). markStoryCompleted is pure + dedupes, so a
  // re-read is an idempotent no-op. MA-831: written through the progress
  // writer (synchronous, never inside a React updater), on the stored record
  // rather than this render's `progress`. The writer reports a failed storage
  // write (quota); retry once, then surface to Sentry (non-blocking) so the
  // kid always exits cleanly even if the write lost.
  function markStoryRead(slug) {
    if (!slug) return;
    const childId = activeChildIdRef.current;
    const r = progressWriter.write(childId, (p) => markStoryCompleted(p, slug));
    if (!childId || !r.changed || r.ok) return;
    if (!progressWriter.flush(childId)) { // retry once
      captureSupabaseError(
        "story completion not persisted",
        new Error("completedStories missing slug after save + retry"),
        { slug, childId },
      );
    }
  }
  function resetProgress() {
    if (activeChildId) { resetProgressForChild(activeChildId); progressWriter.forget(activeChildId); }
    clearActiveSession();
    setProgressValue(defaultProgress(), activeChildId); setScreen("home");
  }
  // S2-2: the core "make this child active" state reload — update the
  // canonical store + React active-id, reload THIS child's progress, and
  // refresh the registry. No navigation: parent surfaces (KidSwitcher) switch
  // in-place and must NOT be bounced to the kid home.
  function switchActiveChild(id) {
    setActiveChildIdStore(id);
    setActiveChildIdState(id);
    setProgressValue(progressWriter.baseFor(id), id); // MA-831: shows a write that has not stored yet
    setRegistry(listChildrenForUserStore(user?.id));
  }
  // Make a child active and clear any session (no navigation).
  function selectChild(id) {
    switchActiveChild(id);
    clearActiveSession();
  }
  // Kid-side pick on /play/who — MA-853: lands on /play in BOTH device modes.
  // It used to route to "/", whose front door sends a personal device to the
  // auto-unlocked /parent, so a child's own name tap opened the dashboard.
  function pickChild(id) {
    selectChild(id);
    router.navigate({ to: "/play", search: (prev) => prev });
  }
  // Cold-boot "Who's playing?" gate (no active child yet). Shown over a kid
  // route, a pick lands on /play (MA-853 RED round 1 S1: on a personal device
  // "/" would send the child to /parent). Shown anywhere else (e.g. over
  // /parent) it keeps the "/" front door, so a parent's own phone still
  // opens on Progress (MA-03; D-PUX-3).
  function selectChildHome(id) {
    selectChild(id);
    if (classifyGatePath(location.pathname) === "kid") {
      router.navigate({ to: "/play", search: (prev) => prev });
    } else {
      setScreen("home");
    }
  }
  function startAddChild() {
    // Clear active child so the onboarding flow renders for a new child.
    setActiveChildIdState(null);
    setProgressValue(defaultProgress(), null);
    clearActiveSession();
  }
  // Delete a child from the local registry + repoint React state. The
  // CLOUD soft-delete (deactivateStudent on the kid's supabaseStudentId)
  // is fired by the caller (SettingsKids) — App.jsx owns only the LOCAL
  // store + the active-pointer/last-kid React-state sync here.
  //
  // removeChildStore already drops the per-child progress key + the
  // local attempt/milestone mirrors (B-2/G-6) and, when
  // the deleted kid was active, repoints registry.activeChildId to
  // remaining[0]?.id || null. We mirror that into React state:
  //   - deleted kid was active → load the next kid's progress (or, if
  //     none remain, defaultProgress → the gate ladder falls to the
  //     no-kids / onboarding state gracefully, no crash).
  //   - deleted kid was NOT active → active pointer is untouched; just
  //     refresh the registry so the list re-renders without that kid.
  function deleteChild(childId) {
    if (!childId) return;
    const wasActive = activeChildId === childId;
    removeChildStore(childId);
    progressWriter.forget(childId);
    const filtered = listChildrenForUserStore(user?.id);
    setRegistry(filtered);
    if (wasActive) {
      const nextId = filtered.children[0]?.id || null;
      clearActiveSession();
      setActiveChildIdStore(nextId);
      setActiveChildIdState(nextId);
      setProgressValue(loadProgressForActiveChild(nextId), nextId);
      setScreen("home");
    }
  }

  // MA-582 mislink-repair (R14) — the Unlink repair: clears a local kid's
  // cloud link (childrenStore.clearSupabaseStudentLink) so its
  // activeChild.supabaseStudentId no longer feeds useEngineChildId() at a
  // foreign students.id, WITHOUT touching the kid's local progress. Hoisted
  // like deleteChild just above it (TDZ trap: appStateApiRef.current is
  // assigned at module-render time, before this file's own function
  // expressions would exist — a function DECLARATION is hoisted and safe).
  //
  // Deliberately narrow (R14): does NOT repoint the active child, does NOT
  // reload progress, and does NOT navigate. If the unlinked kid happens to
  // be ACTIVE, clearActiveSession() clears the live session so an in-flight
  // screen's remaining writes aren't hanging onto a session keyed off the
  // now-cleared link — the SAME clearActiveSession() deleteChild calls above
  // (R7 parity) — but the engine-read self-correction comes for free from
  // activeChild.supabaseStudentId now resolving to null on the next render,
  // not from any repoint this function performs.
  function clearKidCloudLink(childId) {
    const cleared = clearSupabaseStudentLinkStore(childId);
    if (!cleared) return;
    setRegistry(listChildrenForUserStore(user?.id));
    if (activeChildId === childId) clearActiveSession();
  }

  // MA-183 — one merge per (session, student id). Without this guard, a
  // same-device KidSwitcher round trip (Kid A -> Kid B -> back to Kid A)
  // re-fires this effect on A's ORIGINAL id transition and re-applies A's
  // cloudStudent snapshot from the earlier fetch — silently reverting any
  // local edit a parent made in between (e.g. flipping the MA-183 auto-read
  // toggle) even though nothing left this device. Merging only the FIRST
  // time each student id resolves this session still covers "second device
  // after sync" (a fresh session always starts with an empty set), while a
  // same-session switch-away-and-back no longer clobbers.
  //
  // MA-395 (engine-truth-2026-09-08.md §E2, secondary gap) — WIDENED from
  // "id seen" to "id + (assigned, suppressed) stamps seen" via
  // shouldMergeCloudStudent (App/cloudMergeGuard.js). An id-only guard also
  // blocked a genuinely NEWER cloud snapshot from ever re-merging within the
  // same session — harmless while `students` only ever changed at boot, but
  // Fix 2 (useCloudSync's visibility/focus re-pull) can now hand this effect
  // a fresh `cloudStudent` mid-session, and the old guard would silently
  // swallow it. The predicate is pure and unit-tested (cloudMergeGuard.test.mjs)
  // specifically to keep the MA-183 protection exactly intact while
  // unblocking a real stamp change. Map<studentId, compositeStampKey> —
  // still cleared implicitly by a fresh session (a new Map per mount).
  const cloudMergedStudentIdsRef = useRef(new Map());
  // MA-395 — set by the setProgress updater below (once per effect run) so
  // the invalidateQueries call, a real side effect, can live OUTSIDE the
  // updater while still reading its outcome. Mirrors pendingLevelsPushRef's
  // "real side effect lives outside setProgress" rationale below.
  const assignedSkillsChangedRef = useRef(false);
  // MA-375 — set by the setProgress updater below when the pull-side stamp
  // compare (resolveLevelsPullMerge) decides LOCAL wins and the cloud row
  // needs to catch up (e.g. an offline level override the previous session
  // couldn't push). Read outside the updater, same "real side effect lives
  // outside setProgress" rationale as assignedSkillsChangedRef above.
  const pendingLevelsPushRef = useRef(null);
  const pendingAutoReadPushRef = useRef(null); // MA-792
  const pendingMascotPushRef = useRef(null); // MA-1024
  const pendingAgePushRef = useRef(null); // MA-771
  // MA-395 — same shape as pendingLevelsPushRef, for the assignedSkillIds
  // stamp-guarded push (resolveAssignedSkillsPullMerge's shouldPushLocal).
  const pendingAssignedSkillsPushRef = useRef(null);
  // MA-395 (suppressed-skill-ids-cloud-sync slice) — same shape as
  // assignedSkillsChangedRef/pendingAssignedSkillsPushRef above, for
  // suppressedSkillIds (resolveSuppressedSkillsPullMerge).
  const suppressedSkillsChangedRef = useRef(false);
  const pendingSuppressedSkillsPushRef = useRef(null);
  useEffect(() => {
    if (!cloudStudent) return;
    const cloudStamps = {
      assignedSkillIdsUpdatedAt: cloudStudent.assigned_skill_ids_updated_at ?? null,
      suppressedSkillIdsUpdatedAt: cloudStudent.suppressed_skill_ids_updated_at ?? null,
      mascotId: cloudStudent.mascot_id ?? null, // MA-1024 (D-1018-18): a newer pal re-runs the merge
    };
    if (!shouldMergeCloudStudent(cloudMergedStudentIdsRef.current, cloudStudent.id, cloudStamps)) return;
    cloudMergedStudentIdsRef.current.set(cloudStudent.id, stampKey(cloudStamps));
    writeProgressForChild(activeChildId, (prev) => {
      // MA-858 (D-1008-28): the merge itself lives in App/mergeCloudStudent.js
      // (moved verbatim, behaviour-neutral); this updater only stamps the
      // pending pushes and change flags it returns into the refs below.
      const merged = mergeCloudStudentIntoProgress({ prev, cloudStudent, localChildId: activeChildId, registry });
      pendingAgePushRef.current = merged.pushes.age;
      pendingAutoReadPushRef.current = merged.pushes.autoRead;
      pendingMascotPushRef.current = merged.pushes.mascot;
      assignedSkillsChangedRef.current = merged.changed.assigned;
      pendingAssignedSkillsPushRef.current = merged.pushes.assigned;
      suppressedSkillsChangedRef.current = merged.changed.suppressed;
      pendingSuppressedSkillsPushRef.current = merged.pushes.suppressed;
      pendingLevelsPushRef.current = merged.pushes.levels;
      return merged.progress;
    });
    // Read outside setProgress (React may invoke a state updater more than
    // once, e.g. under StrictMode double-render) so the cache invalidation
    // — a real side effect — fires exactly once per merge, gated on the
    // ref the updater just stamped.
    if (assignedSkillsChangedRef.current || suppressedSkillsChangedRef.current) {
      void queryClient.invalidateQueries({ queryKey: qk.todaysMission.all });
    }
    // MA-375 — the cloud row is behind a local override the previous session
    // couldn't push (or has never seen it at all). Push now (covers "already
    // online at boot") with a one-shot reconnect retry (covers "offline at
    // boot, reconnects later this session") — pushLevelsWithRetry is the ONE
    // reconnect mechanism, shared with ParentSettingsSections.tsx's explicit
    // apply-level push.
    // MA-792 — remember what the cloud holds (revert target for a permanent rejection).
    noteAutoReadCloudValue(cloudStudent.id, cloudStudent.auto_read_enabled);
    // MA-792 — re-push an auto-read toggle a previous session couldn't land.
    if (pendingAutoReadPushRef.current) {
      const push = pendingAutoReadPushRef.current;
      pendingAutoReadPushRef.current = null;
      const userIdAtStart = user?.id ?? null;
      // RED M1: completion is child-scoped through the LIVE api (never this
      // render's captured setProgress), so a child switch mid-flight can't
      // write one child's progress into another's slot.
      void syncPendingAutoRead({
        push,
        ctx: {
          getActiveChildId: () => appStateApiRef.current.getLiveActiveChildId(),
          setProgressForChild: (id, fn) => appStateApiRef.current.setProgressForChild(id, fn),
          isChildLinked: (childId, studentId) => appStateApiRef.current.isChildLinked(childId, studentId, userIdAtStart),
          setProgress: (fn) => appStateApiRef.current.setProgress(fn),
          loadStored: loadProgressForActiveChild,
          saveStored: saveProgressForChild,
        },
        updateStudentFn: updateStudent,
        onReconnect: registerOnlineCallback,
        onError: (e) => captureSupabaseError("MA-792 pull-side auto_read_enabled push failed", e, { studentId: push.studentId }),
      });
    }
    // MA-1024 — re-push a pal pick the cloud never confirmed. A returned row
    // carrying the same mascot_id clears the flag for THAT child (id-scoped
    // writer); a PGRST204 strip (no mascot_id in the row) or a failure leaves
    // it set, so the next load retries. Called on EVERY merge (no guard here):
    // pushPendingMascot itself is a no-op for a null push (executed test s3e).
    const mascotPush = pendingMascotPushRef.current;
    pendingMascotPushRef.current = null;
    void pushPendingMascot({
      push: mascotPush,
      updateStudentFn: updateStudent,
      setProgressForChild: (id, fn) => appStateApiRef.current.setProgressForChild(id, fn),
      onError: (e) => captureSupabaseError("MA-1024 pull-side mascot_id push failed", e, { studentId: mascotPush?.studentId }),
    });
    // MA-771 — re-push a local age the cloud never received (same child-scoped
    // ctx + reconnect retry as the auto-read push above).
    if (pendingAgePushRef.current) {
      const agePush = pendingAgePushRef.current;
      pendingAgePushRef.current = null;
      const ageUserIdAtStart = user?.id ?? null;
      void syncPendingAge({
        push: agePush,
        ctx: {
          getActiveChildId: () => appStateApiRef.current.getLiveActiveChildId(),
          setProgressForChild: (id, fn) => appStateApiRef.current.setProgressForChild(id, fn),
          isChildLinked: (childId, studentId) => appStateApiRef.current.isChildLinked(childId, studentId, ageUserIdAtStart),
          setProgress: (fn) => appStateApiRef.current.setProgress(fn),
          loadStored: loadProgressForActiveChild,
          saveStored: saveProgressForChild,
        },
        updateStudentFn: updateStudent,
        onReconnect: registerOnlineCallback,
        onError: (e) => captureSupabaseError("MA-771 pull-side students.age push failed", e, { studentId: agePush.studentId }),
      });
    }
    // MA-784 — restore the diagnostic half of the start-module provenance from
    // the cloud's placement_results (existing table, no schema change) so a
    // fresh device / cleared browser serves what the writing device served.
    // Child-scoped (same ctx as the pushes above), never overwrites a local
    // entry, and fails safe (any error = the legacy rule). Read-only: it
    // creates no students rows (Student-create discipline is untouched).
    {
      const restoreUserIdAtStart = user?.id ?? null;
      void restoreProvenanceForChild({
        studentId: cloudStudent.id,
        localChildId: activeChildId,
        ctx: {
          getActiveChildId: () => appStateApiRef.current.getLiveActiveChildId(),
          setProgressForChild: (id, fn) => appStateApiRef.current.setProgressForChild(id, fn),
          isChildLinked: (childId, studentId) => appStateApiRef.current.isChildLinked(childId, studentId, restoreUserIdAtStart),
          setProgress: (fn) => appStateApiRef.current.setProgress(fn),
          loadStored: loadProgressForActiveChild,
          saveStored: saveProgressForChild,
        },
        fetchRows: fetchPlacementResultsForStudent,
      }).then((r) => {
        // The mission pick is cached (1 h staleTime, key without provenance):
        // a restore that lands after the first selection must refresh it, or
        // the fresh device keeps the pre-restore pick this session (M1).
        if (r === "restored") void queryClient.invalidateQueries({ queryKey: qk.todaysMission.all });
      }).catch(() => {});
    }
    if (pendingLevelsPushRef.current) {
      const { studentId, ...payload } = pendingLevelsPushRef.current;
      pendingLevelsPushRef.current = null;
      void pushLevelsWithRetry(studentId, payload).catch((e) => {
        captureSupabaseError("MA-375 pull-side levels push failed", e, { studentId });
      });
    }
    // MA-395 — same "local is strictly newer, cloud row needs to catch up"
    // case as the levels push above, for assignedSkillIds.
    if (pendingAssignedSkillsPushRef.current) {
      const { studentId, ...payload } = pendingAssignedSkillsPushRef.current;
      pendingAssignedSkillsPushRef.current = null;
      void pushAssignedSkillsWithRetry(studentId, payload).catch((e) => {
        captureSupabaseError("MA-395 pull-side assignedSkillIds push failed", e, { studentId });
      });
    }
    // MA-395 (suppressed-skill-ids-cloud-sync slice) — same "local is
    // strictly newer, cloud row needs to catch up" case as the levels/
    // assignedSkillIds pushes above, for suppressedSkillIds.
    if (pendingSuppressedSkillsPushRef.current) {
      const { studentId, ...payload } = pendingSuppressedSkillsPushRef.current;
      pendingSuppressedSkillsPushRef.current = null;
      void pushSuppressedSkillsWithRetry(studentId, payload).catch((e) => {
        captureSupabaseError("MA-395 pull-side suppressedSkillIds push failed", e, { studentId });
      });
    }
  }, [cloudStudent?.id, cloudStudent?.assigned_skill_ids_updated_at, cloudStudent?.suppressed_skill_ids_updated_at, cloudStudent?.mascot_id, queryClient]);

  useEffect(() => {
    applyViewportMeta(document); // MA-833: keeps viewport-fit=cover (iOS safe-area insets)
    // Typography fidelity fix: font-family is now owned by globals.css
    // (@layer base) + the .font-kid utility, not an inline document.body
    // override. The dynamic Nunito <link> is dropped — index.html already
    // loads Nunito + Fraunces via the Google Fonts <link>.
    document.body.style.background = COLORS.bg; document.body.style.margin = "0";
  }, []);

  // ── Hoisted above the screenContent IIFE (hooks-order fix) ──────────
  // These handlers + derived values + the three hooks below (appStateApiRef,
  // stableApi, appStateValue) MUST run on every render. They previously lived
  // INSIDE the screenContent IIFE *after* the gate-ladder early returns, so
  // while auth/cloud were still loading the IIFE returned <Loading/> early and
  // skipped these hooks — then the first render that passed every gate called
  // 3 extra hooks => "Rendered more hooks than during the previous render",
  // App threw, the ErrorBoundary caught it, and the app was stuck on Loading
  // forever (the e2e non-render). Hoisting them out makes the hook count
  // constant across renders. (Rules of Hooks.)
  // MA-155 (retake-placement-wiring): handleForcePlacement and
  // handleSetDebugAge were removed — both were zero-caller dead code
  // (exposed on the app-state API bag but never invoked by any screen)
  // whose only effect, had they been called, was to route into the
  // now-deleted legacy PlacementScreen/needsPlacement gate.

  function handleResetMissions() {
    setProgress((prev) => ({ ...prev, missions: [], missionDate: null }));
  }

  async function handleSignOut() { try { await signOut(); setScreen("home"); } catch (e) { console.error(e); } }

  // Phase G3 Stage 4: bridge from the new multi-step /onboarding
  // flow's handoff page to pencilwise's data stores. Identical code
  // path to the legacy OnboardingScreen.onComplete; data is sourced
  // from the new flow's collected fields (name/age/locale/mascotId)
  // instead of the old single-screen form.
  async function handleOnboardingComplete(data) {
    // P0 data-loss guard (P-ONBOARD-AUTH-GATE): completion writes a cloud
    // students row keyed by user.id (createCloudStudent). With no auth session
    // that throws on a null user or INSERTs nothing (RLS) -> a local-only kid
    // that vanishes on the next device. Refuse + surface; the caller keeps the
    // draft for retry after sign-in. (Belt-and-suspenders: the gate ladder
    // already blocks /onboarding/* when !user.)
    if (!user?.id) {
      captureSupabaseError(
        "handleOnboardingComplete called without an authenticated user",
        new Error("missing user.id at onboarding completion"),
      );
      return false;
    }
    const baseDefaults = defaultsForAge(Number(data.studentAge || 6));
    const d = applyPriorBoost(baseDefaults, data.parentPriors);
    // P-DUP-STUDENT-FIX — lookup-before-create (mirrors the resolver fix in
    // useActiveStudentId.js, same PR). A re-onboarding for a kid this parent
    // already has (localStorage cleared, fresh device, different browser)
    // would otherwise INSERT a second students row for the same name instead
    // of reusing the existing one — the duplicate-student bug the BUG 2
    // Chrome MCP probe surfaced (22 rows for one auth user). Reuse an
    // UNAMBIGUOUS single name match; 0 or >1 matches fall through to the
    // normal create (>1 stays create to avoid mis-routing onto a sibling's
    // row). Same normalized comparison as childrenStore.autoLinkByNameAge.
    // MA-800: reuse only a same-name row NO local child already holds
    // (pickOnboardingReuseRow); otherwise create — a second kid sharing a name
    // gets its own cloud row instead of sharing the first kid's.
    const reuseRow = pickOnboardingReuseRow(listChildrenStore(), students, data.studentName, user.id);
    const cloudRowRaw =
      reuseRow ||
      (await createCloudStudent(user.id, {
        name: data.studentName,
        age: data.studentAge,
        grade: data.studentGrade,
        mathLevelId: d.mathModuleId,
        readingLevelId: d.readingModuleId,
      }));
    // MA-800 belt: never LINK a kid to a row another local kid already holds
    // (createStudent's 23505 recovery returns the existing same-name row). The new
    // kid is added unlinked (local-only, the existing unlinked behaviour) and the
    // event is reported (ids only). The name-step / Settings check normally stops
    // a same-name add before it gets here.
    const heldPayload = cloudRowRaw?.id ? heldRowPayload(listChildrenStore(), cloudRowRaw.id) : null;
    if (heldPayload) {
      captureSupabaseError(
        "MA-800 onboarding create returned a cloud row already linked to another local child - not linked",
        new Error("held row not linked"),
        heldPayload,
      );
    }
    const cloudRow = heldPayload ? null : cloudRowRaw;
    // Track I.7: mirror parent ability-priors onto the cloud students row so
    // they survive a device change. Placed AFTER cloudRow resolves because it
    // is dual-source (existing name-match OR a fresh createCloudStudent), so
    // this covers both. Fire-and-forget — the local progress write below is
    // the source of truth; skip when there's no cloud id (offline).
    if (cloudRow?.id) {
      void upsertStudentParentPriors(cloudRow.id, data.parentPriors);
    }
    const newChildId = addChildStore({
      name: data.studentName,
      age: data.studentAge,
      ownerUserId: user?.id || null,
      supabaseStudentId: cloudRow?.id || null,
    });
    setActiveChildIdState(newChildId);
    setRegistry(listChildrenForUserStore(user?.id));
    const newProgress = {
      ...defaultProgress(),
      onboardingComplete: true,
      ...data,
      mathModuleId: d.mathModuleId,
      readingModuleId: d.readingModuleId,
      // MA-775: both start modules are AGE-SEEDED (no assessment yet), so
      // the mission pick never exempts them from the age masks.
      moduleProvenance: nextModuleProvenance(null, d, "age"),
      placementStatus: "not_started",
      // MA-771 (flow b) — the onboarding-chosen age is the NEWER value for a
      // reused same-name cloud row (or a row that could not be created): raise the pending flag (the first merge
      // pushes it, retried on failure) instead of letting the row's old age win.
      // Fresh rows were created with this age, so nothing is pending.
      ...onboardingAgePending({ reused: !!reuseRow, cloudRow, localAge: data.studentAge }),
      // MA-1024 (RED r1 M-1) — the onboarding pal step is a pick: pending, so
      // the first merge keeps it over a reused row's older pal and pushes it
      // (createStudent sends no mascot_id, so a fresh row needs the push too).
      ...onboardingMascotPending(data),
    };
    saveProgressForChild(newChildId, newProgress);
    setProgressValue(newProgress, newChildId);
    setAddingChildIntent(false);
    return true;
  }

  // Phase G1: gates passed → hand off to the file-based route tree.
  // AppStateProvider exposes state + handlers to route components via
  // useAppState(). Outlet renders whichever route matches the URL.
  // P2 (Batch 1.5) — kill the whole-tree re-render storm. This value object was
  // rebuilt every App render and provided raw, so every /play /parent /read
  // consumer re-rendered on every cloud/progress/screen tick. Now the FUNCTION
  // members are wrapped once with STABLE identities (a ref always points at the
  // latest implementations → no stale closures), and the value is useMemo'd over
  // only the DATA members, so consumers re-render only when data they read
  // actually changes. (P-PATHHOME-BUG-2-ROUND-2: activeStudentId is the
  // canonical child_attempts read id; ensureActiveStudentId is the write
  // lazy-create/link resolver.)
  const appStateApiRef = useRef(null);
  appStateApiRef.current = {
    getLiveActiveChildId: () => activeChildIdRef.current,
    // MA-792 (RED R2-S4): a queued reconnect write must not fire for a removed
    // child, a signed-out user or an account switch.
    isChildLinked: (childId, studentId, userIdAtStart) =>
      isChildLinkedFor({ registry, user, childId, studentId, userIdAtStart }),
    // RED R3-S1: id-scoped live setter. Saves under THAT child's key; if the
    // active child changed, transforms that child's STORED progress instead.
    // MA-831: the same writer, so a write for another child touches only that
    // child's key and a write for the live child decides on its stored record.
    setProgressForChild: (id, fn) => writeProgressForChild(id, fn),
    setScreen, setProgress, markStoryRead, ensureActiveStudentId,
    setProgressRaw: (value) => setProgressValue(value, activeChildIdRef.current),
    setActiveSession, setCompletedSession, setPendingConcept, setActiveStorySlug,
    setAddingChildIntent, setActiveChildIdState, switchActiveChild, pickChild, setRegistry,
    syncToCloud: syncPickToCloud, // MA-1024: the pick path (MascotPicker) clears its pending flag
    handleConceptAcknowledged,
    handleResetMissions,
    handleSignOut, resetProgress, handleOnboardingComplete, deleteChild,
    clearKidCloudLink,
  };
  const stableApi = useMemo(() => {
    const api = {};
    for (const k of Object.keys(appStateApiRef.current)) {
      api[k] = (...args) => appStateApiRef.current[k](...args);
    }
    return api;
  }, []);
  // MA-581 — the OWNER-SCOPED truth, exposed so family-facing pickers can
  // intersect their localStorage-derived kid lists against it at READ time.
  //
  // ⚠️WHY READ-TIME AND NOT A REPAIR WRITE: the obvious fix is to walk the
  // registry and null out any `supabaseStudentId` not in this set. An
  // adversarial review proved that destructive: it would wipe every KIOSK
  // SHADOW link on an armed classroom device (shadows carry a non-null
  // `enrollment_id`, which `fetchStudents` filters out, so they are never in
  // this set) and mint a duplicate cloud row per roster tile; it would
  // resurrect a cross-device soft-delete as a duplicate (`is_active:false` is
  // filtered too); and it would have a coin-flip chance of being a no-op for a
  // teacher who owns zero family students. Intersecting at read time fixes the
  // picker with zero data loss and no wipe mode.
  const ownedStudentIds = useMemo(() => new Set(students.map((s) => s.id)), [students]);
  // True only when a fetch SUCCEEDED, has SETTLED, and the list we hold belongs
  // to the user now signed in. `students` alone cannot answer the last part —
  // `user?.id` can change before `students` does, so during an account switch a
  // consumer would measure account B against account A's rows.
  const studentsTrusted =
    !!user?.id && !cloudLoading && cloudError == null && studentsOwnerId === user.id;
  // MA-582 — the narrow trust signal PendingClaimCodeCard's gate module needs
  // and nothing else consumes today: `studentsOwnerId` (to catch a positive
  // cross-account distrust window) and `cloudError` (the returning-parent
  // failed-boot hole a prior round of this fix missed — see
  // pendingClaimCardGate.ts's header). One derived object rather than two
  // loose fields on appStateValue, per REV 3 delta 6.
  const claimTrust = useMemo(
    () => ({ studentsOwnerId, cloudError }),
    [studentsOwnerId, cloudError],
  );
  const appStateValue = useMemo(
    () => ({
      progress,
      user, registry, activeChildId,
      ownedStudentIds, studentsTrusted,
      claimTrust,
      // MA-800: true while the parent is explicitly adding ANOTHER kid (Who's-playing
      // "Add child"); useFinishOnboarding must not adopt an existing kid then.
      addingChildIntent,
      activeStudentId: activeSupabaseStudentId,
      activeSession, pendingConcept, activeStorySlug,
      ...stableApi,
    }),
    [
      progress,
      user, registry, activeChildId, activeSupabaseStudentId,
      ownedStudentIds, studentsTrusted,
      claimTrust, addingChildIntent,
      activeSession, pendingConcept, activeStorySlug,
      stableApi,
    ],
  );
  // All screen-resolution logic flows through `screenContent` so the
  // single MascotProvider wrap at the bottom of App applies to every
  // gate (Loading, Login, WhosPlaying, Onboarding, MascotPick) as well
  // as the route switch. Pre-context, gates returned directly and
  // bypassed the provider — meaning some gated screens got the mascot
  // via drilled props instead of context.
  const screenContent = (() => {
  // MA-09: the public legal pages (/privacy, /terms, /coppa-notice) bypass
  // the ENTIRE gate ladder — auth, registry, progress — because a parent must
  // be able to read them BEFORE signing in and BEFORE the COPPA consent step
  // links to them. Unlike the qa-* bypass below, this is NOT dev-gated: these
  // routes are public by design. Mirrors the qa pattern: render the matching
  // route via <Outlet/> inside `.sb-root` so the scoped preflight applies.
  if (
    location.pathname === "/privacy" ||
    location.pathname === "/terms" ||
    location.pathname === "/coppa-notice" ||
    location.pathname === "/dpa"
  ) {
    return (
      <AppStateProvider value={{}}>
        <div className="sb-root sb-safe sb-safe-bottom h-full">
          <Outlet />
        </div>
      </AppStateProvider>
    );
  }
  // MA-238 ma241-redemption-route slice: /join/:code is the send-home
  // card's QR/typed-code destination. A parent has NOT signed in yet when
  // they land here, so — like the legal pages above — it bypasses the
  // ENTIRE gate ladder. Unlike /privacy et al. it is not a canonical legal
  // page (legalRoutesGuard.mjs's 3-route list is unaffected), but the same
  // public-by-design reasoning applies: it must render before auth, and is
  // NOT dev-gated. See JoinRedemption.tsx for what the route itself does.
  // MA-291: /claim/:code is the parent-activation deep link (per-student
  // claim code) — same public-by-design reasoning as /join/ above; the
  // REAL redemption runs post-auth on the parent dash (PendingClaimCodeCard).
  if (location.pathname.startsWith("/join/") || location.pathname.startsWith("/claim/")) {
    return (
      <AppStateProvider value={{}}>
        <div className="sb-root sb-safe sb-safe-bottom h-full">
          <Outlet />
        </div>
      </AppStateProvider>
    );
  }
  // F4 — QA-ITEM-ROUTE-SPEC: /qa-item is a public, no-PII content-render surface
  // (curriculum items only) so the build agent can verify question visuals on a
  // deployed PREVIEW without auth. It is GATED to non-production deploys
  // (qaRoutesEnabled) because the render exposes the item's answer + options —
  // the full answer key — which must never be reachable on the production app.
  // On production this branch renders only a plain "QA route not available"
  // message (never the question/answer); the route component is belt-and-braces
  // gated too (src/routes/qa-item.tsx). See docs/adr/026-qa-item-preview-only.md.
  if (location.pathname === "/qa-item") {
    return (
      <AppStateProvider value={{}}>
        <div className="sb-root sb-safe sb-safe-bottom h-full">
          {qaRoutesEnabled() ? (
            <Outlet />
          ) : (
            <div className="p-8 text-lg" data-qa-item-blocked>
              QA route not available.
            </div>
          )}
        </div>
      </AppStateProvider>
    );
  }
  // Phase G2: /qa-gallery bypasses the entire gate ladder. It's a
  // design-system acceptance surface that must render with no auth,
  // no registry, no progress — so reviewers and e2e tests can verify
  // shadcn primitives without going through the onboarding flow.
  // R-1: QA acceptance surfaces are dev/e2e-only. Gate the gate-ladder
  // bypass behind import.meta.env.DEV so a production kid/parent can never
  // reach a raw QA route (the route components are belt-and-braces gated too).
  if (import.meta.env.DEV && (location.pathname === "/qa-gallery" || location.pathname === "/qa-pal" || location.pathname === "/qa-handwriting")) {
    return (
      <AppStateProvider value={{}}>
        {/* qa-* are new storybook UI → scoped preflight applies. */}
        <div className="sb-root sb-safe sb-safe-bottom h-full">
          <Outlet />
        </div>
      </AppStateProvider>
    );
  }
  // MA-582 (D4): the predicate itself lives in bootGateGuard.js now, pinned
  // by an executed unit test — sourceTextAssertionRatchet is at its cap and
  // no React render harness exists, so a source-text pin on this expression
  // was never an option (see that module's header comment).
  if (shouldHoldForBoot({ authLoading, allowlistLoading, cloudLoading, bootHydrated })) return <Loading />;
  // H-A5: the gate screens below (Login / WhosPlaying / MascotPick /
  // Placement) are token-TSX now — they render inside `.sb-root` so the
  // scoped Tailwind preflight applies to gate rendering too (they are
  // also individually UA-proofed, so this is belt-and-braces).
  const gate = (node) => <div className="sb-root sb-safe">{node}</div>;
  if (!user) return gate(<LoginScreen />);

  // MA-271: an allowlist-endpoint outage for a parent PREVIOUSLY confirmed
  // allowlisted must not read as "you were never approved" — hold them at a
  // recoverable retry surface instead. A brand-new/never-approved parent
  // hitting the SAME outage still falls through to the honest
  // PrivateBetaScreen below (outage stays false for them — see
  // allowlistOutageGuard.js's asymmetric guard). Rendered without gate()'s
  // .sb-root wrapper, matching the OTHER CloudErrorRetry call site below
  // (the component is self-contained-inline-styled).
  if (!allowlisted && allowlistOutage) {
    return (
      <CloudErrorRetry
        context="allowlist"
        onRetry={retryAllowlistCheck}
        error={allowlistError}
        onSignOut={() => { signOut().catch((e) => console.error(e)); }}
      />
    );
  }

  // MA-03b: private-beta allowlist gate.
  // allowlistLoading was already handled above (shows <Loading />), so here
  // allowed is the settled boolean. Fires for ALL authenticated users —
  // onboarding state / cloud students don't matter; the gate sits before
  // every subsequent branch so no child PII is reached without approval.
  if (!allowlisted) {
    return gate(
      <PrivateBetaScreen
        email={user.email}
        onSignOut={() => { signOut().catch((e) => console.error(e)); }}
      />,
    );
  }

  // P-CROSS-DEVICE-HYDRATION (#20): the hydration effect runs AFTER this
  // render commits. The render where cloudLoading first flips false (with
  // students freshly arrived) still has an EMPTY local registry — so without
  // this guard the needsOnboarding gate below would flash an
  // OnboardingRedirector that navigates to /onboarding and sticks, defeating
  // the whole fix. Hold on Loading for that one extra render; once the effect
  // commits, registry.children > 0 (or the ref latches) and this clears.
  // Brand-new users (no cloud students) and offline/RLS-failed fetches
  // (students stays empty — e.g. the dev-bypass e2e user) skip this entirely
  // and proceed to onboarding exactly as before.
  const pendingCloudHydration =
    students.length > 0 &&
    registry.children.length === 0 &&
    !hasHydratedThisSessionRef.current;
  if (pendingCloudHydration) return <Loading />;

  // ── Multi-child gate: Who's playing? ──
  // Show the picker if (a) more than one child exists and none is currently
  // selected, or (b) the user just chose "Add child" from the switcher.
  // A single registered child auto-selects (no friction on app launch).
  //
  // MA-553 — `registry.children` itself stays UNFILTERED (kiosk-tagged
  // entries included — see listChildrenForUser's own comment in
  // childrenStore.js: App.jsx's activeChild lookup below depends on that),
  // but the family "who's playing" picker is the family read path, so both
  // the gate condition AND the list handed to WhosPlayingScreen must agree
  // on the SAME filtered view — otherwise a device holding only kiosk-
  // tagged entries (no real family kids) would open the picker gate and
  // then render it with zero tiles.
  const familyChildren = registry.children.filter((c) => !isKioskTaggedChild(c));
  const showWhosPlaying = !activeChildId && familyChildren.length > 0 && !addingChildIntent;
  if (showWhosPlaying) {
    return gate(<WhosPlayingScreen
      // MA-867 — each tile carries that kid's own stored pal (progress.mascot.id).
      children={familyChildren.map((c) => ({ ...c, mascot: loadProgressForChild(c.id, () => null)?.mascot?.id ?? null }))}
      onPick={selectChildHome}
      onAdd={() => {
        // MA-544 defense-in-depth: addKidRedirect below already swaps out
        // the tile this onClick lives on, so this branch is not reachable
        // through the UI — guarded anyway (same rationale as the other two
        // gated entry points' onAddKid/handoff guards).
        if (familyAddKidBlocked) return;
        // Mark the explicit add intent so the next render skips this gate
        // and falls through to OnboardingScreen instead of looping back.
        setAddingChildIntent(true);
        setProgressValue({ ...defaultProgress(), onboardingComplete: false }, null);
        setActiveChildIdState(null);
      }}
      // MA-544 — a class-owning teacher sees honest routing copy instead of
      // the "+ Add child" tile (see teacherAddKidGate.ts's header).
      addKidRedirect={familyAddKidBlocked}
    />);
  }

  // Phase G3 Stage 4: dual-path onboarding.
  //
  // - When the URL is /onboarding/*, fall through to the route
  //   tree so the multi-step storybook-port flow renders.
  // - When the URL is anywhere else AND onboarding is needed,
  //   render the legacy inline OnboardingScreen (single-screen
  //   form, same data path as before Phase G3).
  //
  // The pure "router.navigate to /onboarding" gate-redirect was
  // explored during Stage 4 but defers to Phase H — the new flow
  // visually ports correctly but isn't yet data-complete across
  // all 5 kid steps + diagnostic + parent-results. Until Phase H
  // wires the new flow to the same data path the inline screen
  // owns end-to-end (handoff already calls handleOnboardingComplete,
  // but step-by-step persistence isn't real), keep the inline
  // gate as the default + the new routes as opt-in.
  const needsOnboarding = !cloudStudent && !progress.onboardingComplete;
  // P-AUTH-GATE-AND-ONBOARDING-DRAFT (QA-AUDIT Area 1 P1): before treating an
  // un-onboarded local state as "this parent must onboard," check whether it's
  // actually the cloud-fetch-failure fall-through. If the initial students
  // fetch FAILED (cloudError) AND we can prove a returning provisioned parent
  // (auth account older than the threshold), their kids exist in the cloud and
  // we simply couldn't read them — HOLD them at a recoverable retry surface
  // instead of re-onboarding. Brand-new users + the ?dev=1 e2e user (no/recent
  // created_at) fall through to onboarding exactly as before. See
  // onboardingHoldGuard.js for the inverted-heuristic rationale.
  if (needsOnboarding && shouldHoldForCloudError({ cloudError, user })) {
    return (
      <CloudErrorRetry
        onRetry={refreshStudents}
        error={cloudError}
        onSignOut={() => { signOut().catch((e) => console.error(e)); }}
      />
    );
  }
  // MA-394: the fetch DIDN'T fail — it succeeded with zero active rows (e.g.
  // both of this parent's students were deactivated, MA-392/393). cloudError
  // is null so the guard above never fires; without this second check a
  // returning parent falls straight into onboarding/signup. Same
  // inverted-heuristic contract: HOLD only on positive proof of a returning
  // account, otherwise fall through to onboarding unchanged (see
  // onboardingHoldGuard.js).
  //
  // MA-629 §3b — `zeroActiveRecovery` is computed inside useCloudSync (which
  // already holds every input this predicate needs, `hasAnyOwnedStudent`
  // included) and passed with NO ARGUMENTS here, so this call site cannot
  // forget one. The comparison itself lives in `shouldHoldZeroActive`
  // (`onboardingHoldGuard.js`), NOT inlined here — with the source-text
  // ratchet at its cap, no new pin can read App.jsx and no render harness
  // exists, so an inline `!== false` would be pinned by nothing: a later
  // edit to a bare `&& zeroActiveRecovery` would ship green through every
  // suite in the repo. `shouldHoldZeroActive`'s `!== false` is load-bearing,
  // not defensive noise: it is the ONLY value that stops the hold. A
  // missing/renamed return key reaches here as `undefined`, and
  // `shouldHoldZeroActive(undefined)` is `true` — so an unwired return key
  // fails CLOSED (still holds) rather than silently deleting MA-394's entire
  // hold class. Do not inline this back to a bare `&& zeroActiveRecovery`.
  if (needsOnboarding && shouldHoldZeroActive(zeroActiveRecovery)) {
    return (
      <CloudErrorRetry
        context="zero-active"
        onRetry={refreshStudents}
        error={null}
        onSignOut={() => { signOut().catch((e) => console.error(e)); }}
      />
    );
  }
  // MA-580 (B) — /teacher escapes the onboarding gate. ⭐WITHOUT THIS, A
  // CLASS-OWNING ACCOUNT WITH NO FAMILY KID IS IN A CLOSED LOOP: needsOnboarding
  // sends them to /onboarding; onboarding.kid.handoff's `blocked` branch (the
  // MA-544 family-add gate, which MA-557 re-keyed onto the ACCOUNT fact rather
  // than the device role) renders a single escape — <Link to="/teacher"> — and
  // /teacher does not start with "/onboarding", so this very redirect bounces
  // them straight back. Onboarding → roster link → onboarding, forever.
  //
  // Found live 2026-08-28 while diagnosing the founder's own claim-code
  // failure: he owns a class, so he is `blocked` regardless of the parent role
  // ClaimRedemption sets, and a fresh browser strands him. Same shape as the
  // /join/ and /claim/ bypass immediately above — a surface a gated user has a
  // legitimate reason to reach before onboarding exists.
  if (
    needsOnboarding &&
    !location.pathname.startsWith("/onboarding") &&
    !location.pathname.startsWith("/teacher")
  ) {
    // T0 cutover: the new multi-step /onboarding flow is the only path.
    // Redirect un-onboarded users into it instead of rendering the legacy
    // inline <OnboardingScreen>. (Legacy screen file deletion is deferred
    // to the follow-up cleanup PR.)
    return <OnboardingRedirector />;
  }
  // MA-394 (b): the symmetric exit — cross-device hydration can settle AFTER
  // a parent was already routed onto /onboarding (hydration runs one render
  // after the gate ladder first sees an empty local registry). Mirrors the
  // entry redirect above one-shot-for-one-shot; see
  // shouldExitOnboardingAfterHydration's doc comment for why this is scoped
  // to the hydration latch rather than any onboardingComplete flip.
  if (
    shouldExitOnboardingAfterHydration({
      hydratedThisSession: hasHydratedThisSessionRef.current,
      needsOnboarding,
      pathname: location.pathname,
    })
  ) {
    return <OnboardingExitRedirector />;
  }
  // If needsOnboarding && in /onboarding/* — fall through to the
  // main AppStateProvider + Outlet below. Route tree handles steps.

  // Mascot pick gate — between onboarding and placement. The kid picks
  // a pencil pal once, then it sticks (kernel #6).
  // T0 cutover: while the kid is still inside the new /onboarding/* flow,
  // don't let the legacy MascotPick/Placement gates intercept the route
  // Outlet — the new pal step + diagnostic satisfy these (mascot carried
  // through the handoff; placementStatus flipped in the diagnostic).
  const inOnboardingFlow = location.pathname.startsWith("/onboarding");
  // MA-324: KidSwitcher.switchTo (a PARENT surface) reloads the switched-to
  // kid's progress in place without navigating (see switchActiveChild's own
  // comment: "parent surfaces ... must NOT be bounced to the kid home"). The
  // very next render re-evaluated this gate against the new kid's progress
  // and, for a kid who never picked a mascot, hijacked the parent into the
  // kid-facing MascotPickScreen — the identical flaw the sibling placement
  // gate above was removed for. shouldShowMascotGate (App/mascotGate.js) is
  // the pure, unit-tested decision core; it scopes the gate to the kid's own
  // surfaces (mirrors the MA-155 fix) — the kid still gets the one-time pick
  // on their own next session-entry, since /parent/* is excluded, not /play/*.
  if (shouldShowMascotGate({
    onboardingComplete: progress.onboardingComplete,
    mascot: progress.mascot,
    pathname: location.pathname,
  })) {
    return gate(<MascotPickScreen onPick={({ id }) => {
      const { value: updated } = setProgress((p) => withMascotPick(p, id)); // MA-831, MA-1024
      void syncPickToCloud(updated);
    }} />);
  }

  // MA-155 (retake-placement-wiring): the needsPlacement gate + legacy
  // <PlacementScreen> render were REMOVED (2026-07-21). This gate fired
  // for any onboarded-but-not-placement-resolved child regardless of
  // screen/route (e.g. adding a second kid via Parent Settings' "+ Add a
  // kid" form sets the new kid's placementStatus to "not_started" while
  // staying on /parent/settings, and the very next render tripped this
  // gate) — but the flow it gated into was already broken: tapping
  // "Start quick check" (handlePlacementStart) navigated into
  // /play/session (MathSession.tsx), which has no concept of the
  // "placement" domain object and never resolves placementStatus off
  // "in_progress", so the gate could never clear itself and silently
  // dead-ended over whatever screen the parent was actually on.
  // PlacementCheckCard (ParentSettingsSections.tsx) is the one live,
  // working "recheck level" surface; a freshly-hydrated cross-device kid
  // (crossDeviceHydration.js) simply gets age-band defaults with no
  // automatic nudge now, same as a kid who skipped placement always did.

  // The new storybook UI (.tsx screens under /play, /parent, /pal,
  // /read, /library, /journey, /onboarding, /qa-*) renders inside
  // `.sb-root` so the scoped Tailwind preflight in globals.css applies.
  // Legacy routes (and the gate shell, which renders outside this
  // Outlet) get NO wrapper → byte-identical to today. Step 2 (the
  // Phase-H flip) drops this wrapper + the scope.
  // MA-343: /journey added — without it the third tab renders a bare
  // <Outlet/> outside .sb-root with no tab bar (RT-1, CRITICAL).
  const sbActive = /^\/(play|parent|pal|read|library|journey|onboarding|qa-)/.test(location.pathname);
  // Persistent bottom tab bar — shows only on the kid hub surfaces
  // (storybook home, the path, the read shelf).
  // Hidden during focused activities (session, reading, page reveal,
  // recap, coach, locked teaser, daily goal) and on every parent / gate /
  // legacy route, mirroring Lovable's PhoneFrame `nav` gating.
  // MA-342: /pal is no longer a hub tab (retired — see BottomTabBar.tsx),
  // so it's dropped here too; visiting /pal directly (the header pal
  // chip, or a deep link) now renders WITHOUT the tab bar, same as any
  // other non-hub screen.
  const TAB_BAR_PATHS = new Set([
    "/play",
    "/journey",         // MA-343: the Journey tab (was /play/journey — the
                         // route moved to a top-level peer of /play + /read)
    "/read",
    "/library",        // story catalog — keeps the tab bar for back-navigation
  ]);
  const showTabBar = TAB_BAR_PATHS.has(location.pathname);
  // The four focused exercise/session surfaces. On iPad (>=md) these must
  // fit ONE page with no document scroll so a tap/trackpad gesture selects
  // an answer instead of panning the page. We give only these routes a
  // fixed 100dvh shell that clips-to-fit at md+ but still scrolls on the
  // smaller iPhone (<md) so nothing is ever clipped. Every other non-hub
  // route keeps its original `.sb-root h-full` behavior, byte-identical.
  const isSession = __isSessionPath;
  // MA-347: active-child's first name for the offline banner's copy — same
  // extraction PathHome uses for its greeting (progress.studentName, first
  // token only). Mounted as a sibling of the whole sbActive ternary (never
  // nested inside the isSession/FitToViewport branch below) so it always
  // renders as a true viewport-fixed overlay — a `transform` on the session
  // shell's scaled inner div would otherwise become its containing block
  // and clip it on iPad.
  // MA-750: the banner reports its height and the frame below moves down by
  // it (an in-flow spacer + a frame that is that much shorter), so the fixed
  // banner covers only the reserved strip — never the session Exit, a hub
  // header or a story's back control. Online, every style is undefined and
  // no spacer renders: the online DOM is unchanged.
  // MA-907: the default "Student" placeholder is not a name — the helper
  // returns null for it, so the banner uses its neutral wording.
  const offlineBannerChildName = offlineBannerFirstName(progress);
  const inset = offlineInsetLayout(offlineInset);
  return (
    <AppStateProvider value={appStateValue}>
      <OfflineBanner childName={offlineBannerChildName} holdingWrites={outboxHolding} onHeightChange={setOfflineInset} />
      {inset.spacerStyle && (
        <div data-testid="offline-banner-inset" aria-hidden="true" style={inset.spacerStyle} />
      )}
      {sbActive ? (
        showTabBar ? (
          // Hub layout: scrollable content above a pinned tab bar.
          // MA-558: KioskModeChip mounts here ONCE (not per-route) so an
          // armed device shows the "Class mode" indicator across all four
          // hub routes. `relative` lets the chip float (position:absolute)
          // in this frame's own bottom-right corner, pinned above the tab
          // bar, WITHOUT reserving layout space or shrinking the Outlet's
          // scroll area — the iPad one-page-no-scroll priority on the
          // session routes below is untouched (this branch is disjoint
          // from that one), and nothing here shifts height on arm/disarm.
          <div className="sb-root sb-safe relative flex h-full flex-col" style={inset.frameStyle}>
            <div role="main" className="min-h-0 flex-1 overflow-y-auto">
              <Outlet />
            </div>
            <KioskModeChip />
            <BottomTabBar />
          </div>
        ) : isSession ? (
          // Session layout: hybrid one-page fit via the useFitToViewport HOOK.
          // The shell is a PLAIN <div> (same element type as the non-session
          // <div> below) so a route change reconciles div->div and the Outlet
          // always swaps (fixes the coach screen failing to mount after the
          // exercise). iPhone (<md) scrolls; iPad/desktop (>=md) scales to fit.
          <div ref={fitViewport.outerRef} className={fitViewport.outerClassName + " sb-safe sb-session-frame"} style={inset.sessionFrameStyle} {...fitViewport.outerDataAttrs}>
            <div
              ref={fitViewport.innerRef}
              role="main"
              className="flex min-h-0 flex-1 flex-col"
              style={fitViewport.innerStyle}
            >
              {/* MA-622 R1 — lets ReadingSession/MathSession/MixedSession
                  (via useFitFreezeKey) report their current item id, so the
                  hook freezes the scale to it instead of rescaling when the
                  trace pad mounts mid-item. */}
              <FitFreezeKeyContext.Provider value={fitViewport.setFreezeKey}>
                <Outlet />
              </FitFreezeKeyContext.Provider>
            </div>
          </div>
        ) : (
          <div role="main" className="sb-root sb-safe sb-safe-bottom h-full" style={inset.frameStyle}>
            <Outlet />
          </div>
        )
      ) : (
        <Outlet />
      )}
    </AppStateProvider>
  );
  })();

  return (
    <MascotProvider id={progress.mascot?.id || null}>
      {/* MA-73a-cap — inert live-QA capture hooks (ADR-047). First child of the
          root provider so it renders on EVERY route + gate state (login-walled,
          loading, error, session). The non-LLM Playwright driver reads these two
          data-* attributes; NO innerText/screenshots are ever read (answer-safety).
          data-build-sha ships on ALL envs INCLUDING PROD (inert, non-attacker-
          controllable commit metadata; MA-73i guard-12 reads it off the prod page —
          a preview-only hook would blind the CI revert net). data-capture-nonce is
          PREVIEW-ONLY + regex-sanitised (attacker-controllable reflected ?qaNonce=);
          captureNonceAttr() returns undefined on prod/malformed → attribute omitted. */}
      <div
        hidden
        aria-hidden="true"
        data-qa-capture-hooks
        data-capture-nonce={captureNonceAttr()}
        data-build-sha={buildShaAttr()}
        data-auth-state={authStateAttr(user)}
        data-front-door-landing={frontDoorLandingAttr(typeof window !== "undefined" && window.location ? window.location.pathname : "")}
        data-qa-local-init={qaLocalInitAttr(
          typeof localStorage !== "undefined" ? (() => { try { return localStorage.getItem("pw_user_role"); } catch { return null; } })() : null,
          typeof localStorage !== "undefined" ? (() => { try { return localStorage.getItem("pw_device_mode"); } catch { return null; } })() : null,
        )}
      />
      {/* Lazy-loaded screens (Settings, Storybook, Dashboard, etc.) need
          a Suspense boundary while their chunk is fetched. Falling back
          to the same <Loading /> spinner the auth path uses keeps the
          UX consistent — the kid sees the pencil bouncing for the
          ~50-200ms it takes to download a screen chunk. */}
      <Suspense fallback={<Loading />}>{screenContent}</Suspense>
      {/* Persistent offline indicator. Shows whenever cloud sync is down
          so the parent knows their kid's progress is local-only until
          the connection recovers. Tiny + top-corner so it doesn't steal
          attention from the active screen. */}
      {/* MA-750: hidden while the offline banner is on screen — one signal. */}
      {cloudOffline && !offlineBannerShowing && (
        <div data-testid="offline-pill" style={{
          // PR-A: pointer-events none so the pill can never intercept a tap
          // meant for the control beneath it, and bottom-LEFT (out of the
          // top-right Exit / parent-header / KidSwitcher hit-zone). Safe-area
          // inset keeps it clear of the iPhone home indicator. It's a passive
          // status indicator, never interactive.
          position: "fixed", left: 8, bottom: "calc(8px + env(safe-area-inset-bottom))",
          zIndex: 999,
          pointerEvents: "none",
          background: COLORS.surface,
          borderLeft: `3px solid ${COLORS.coral}`,
          // A4 (a11y): ink (not textSoft) so the small-bold pill text clears
          // WCAG AA contrast even at 0.85 opacity over the surface.
          color: COLORS.text,
          padding: "4px 10px",
          borderRadius: 6,
          fontSize: 11, fontWeight: 700,
          fontFamily: "inherit",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          opacity: 0.85,
        }}>● offline · saved locally</div>
      )}
      <Toast message={toast?.message} kind={toast?.kind} onDismiss={dismissToast} />
    </MascotProvider>
  );
}

// Loading extracted to src/App/Loading.jsx in Phase G1 so route
// components can reuse the same spinner during lazy-chunk hydration.

// Phase G3 Stage 4: OnboardingRedirector — fires the gate-ladder
// navigation to /onboarding IN A useEffect so the navigate call
// doesn't run during the render phase (same discipline as G1).
// Renders <Loading /> until the router commits the new URL.
function OnboardingRedirector() {
  const router = useRouter();
  useEffect(() => {
    router.navigate({ to: "/onboarding", search: (prev) => prev });
  }, [router]);
  return <Loading />;
}

// MA-394 (b): the reverse-direction one-shot. Navigates to "/" rather than a
// hardcoded destination — the front-door route (src/routes/index.tsx)
// already owns "where does an authenticated, onboarded user land"
// (resolveFrontDoorDestination, keyed on device mode + role), so this stays
// correct if that policy ever changes. Renders <Loading /> for the one
// commit it takes the router to settle, same as OnboardingRedirector.
function OnboardingExitRedirector() {
  const router = useRouter();
  useEffect(() => {
    router.navigate({ to: "/", search: (prev) => prev });
  }, [router]);
  return <Loading />;
}
